import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { useSaveCityOrder, type CityOrderReview, type Saved } from "../../../api/cityOrders";
import DropZone from "../../quotations/reader/DropZone";
import { fileAsBase64 } from "../../quotations/reader/readerApi";
import CheckStep from "./CheckStep";
import ClearStep from "./ClearStep";
import ConfirmStep from "./ConfirmStep";
import Footer from "./Footer";
import { CITY_STEPS, choicesFrom, decisionsBody, holdAt, isStale, pruneChoices, type Choice, type Choices, type CityStep } from "./cityOrderReview";
import { useCityReading } from "./useCityReading";

interface Props {
  /** The bill the job is on, to check a new PO against. */
  boqId: number | null;
  /** An order already on file, to check again from its stored PDF. */
  orderId: number | null;
  onClose: () => void;
  onSaved: (saved: Saved) => void;
}

function messageOf(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/** Focus into the dialog on open, and back to what opened it on close. */
function useDialogFocus() {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    heading.current?.focus();
    return () => {
      if (opener !== null && opener.isConnected) {
        opener.focus();
      }
    };
  }, []);
  return heading;
}

interface SubmitInput {
  orderId: number | null;
  file: File | null;
  review: CityOrderReview | null;
  picked: number | null;
  choices: Choices;
  /** The override's reason once chosen (possibly empty), null when not overriding. */
  override: string | null;
  onSaved: (saved: Saved) => void;
}

/** Saving, once at a time: `saving` is set before the PDF is encoded, so
 * Confirm and Save-later cannot be sent twice, and an encode failure shows. */
function useSubmit(input: SubmitInput) {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const sending = useRef(false);
  const save = useSaveCityOrder();

  async function submit(confirm: boolean) {
    const { orderId, file, review, picked, choices, override } = input;
    if (review === null || sending.current) {
      return;
    }
    sending.current = true;
    setSaving(true);
    setSaveError("");
    try {
      const attachment = file !== null ? { base64: await fileAsBase64(file), filename: file.name } : null;
      const saved = await save.mutateAsync({
        orderId,
        attachment,
        body: {
          boq_id: picked ?? review.boq?.id ?? null,
          decisions: decisionsBody(review, choices),
          overridden: override !== null && override !== "",
          override_reason: override === null || override === "" ? null : override,
          confirm,
          fingerprint: review.fingerprint || null,
        },
      });
      input.onSaved(saved);
    } catch (cause) {
      setSaveError(messageOf(cause));
    } finally {
      sending.current = false;
      setSaving(false);
    }
  }

  return { saving, saveError, setSaveError, submit };
}

/** Reading the City's PO against our BOQ, as a drawer over the job: drop it,
 * see what was held and what differs, decide each difference, confirm. The
 * server reads the PDF again on save and refuses anything the screen let by. */
export default function CityOrderDrawer({ boqId, orderId, onClose, onSaved }: Props) {
  const firstStep: CityStep = orderId === null ? 1 : 2;
  const [step, setStep] = useState<CityStep>(firstStep);
  const [choices, setChoices] = useState<Choices>({});
  const [overriding, setOverriding] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [picked, setPicked] = useState<number | null>(boqId);
  const { file, review, reading, error, pdfUrl, load } = useCityReading(orderId);
  const heading = useDialogFocus();

  // An order on file is checked again as soon as the drawer opens.
  useEffect(() => {
    if (orderId !== null) {
      void load(null, boqId).then((outcome) => outcome.kind === "ok" && setChoices(choicesFrom(outcome.review.decisions)));
    }
    // Once, on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function clearDecisions() {
    setChoices({});
    setOverriding(false);
    setOverrideReason("");
    setSaveError("");
  }

  const { saving, saveError, setSaveError, submit } = useSubmit({ orderId, file, review, picked, choices, override: overriding ? overrideReason.trim() : null, onSaved });

  // A read that fails sends a dropped file back to the drop step; an order on
  // file stays on its check, where it can be read again.
  async function read(source: File | null, boq: number | null, keep = false) {
    const outcome = await load(source, boq);
    if (outcome.kind === "ok") {
      if (keep) {
        setChoices((now) => pruneChoices(outcome.review, now));
      }
      setStep(2);
    } else if (outcome.kind === "failed" && orderId === null) {
      setStep(1);
    }
  }

  function onDrop(next: File) {
    clearDecisions();
    void read(next, picked);
  }

  function onPick(id: number) {
    clearDecisions();
    setPicked(id);
    void read(file, id);
  }

  function reread() {
    setSaveError("");
    setOverriding(false);
    setOverrideReason("");
    void read(file, picked, true);
  }

  function choose(code: string, choice: Choice | null) {
    setChoices((now) => {
      const rest = Object.fromEntries(Object.entries(now).filter(([key]) => key !== code));
      return choice === null ? rest : { ...rest, [code]: choice };
    });
  }

  const hold = reading ? "Reading…" : holdAt(step, review, choices, overriding, overrideReason);
  const canSaveLater = review !== null && step >= 2 && holdAt(2, review, choices, overriding, overrideReason) === null;
  const failedRecheck = orderId !== null && review === null && !reading;

  return (
    <Shell onClose={onClose} step={step} heading={heading}>
      <div className="drawer-body qr-drawer-body">
        <ReadError error={error} retry={failedRecheck ? reread : null} />
        {step === 1 ? <DropStep onFile={onDrop} reading={reading} fileName={file?.name ?? null} /> : null}
        {step === 2 && review !== null ? (
          <CheckStep
            review={review}
            choices={choices}
            onChoose={choose}
            picked={picked}
            onPick={onPick}
            pdfUrl={pdfUrl}
            overriding={overriding}
            overrideReason={overrideReason}
            onOverriding={setOverriding}
            onOverrideReason={setOverrideReason}
          />
        ) : null}
        {step === 3 && review !== null ? <ClearStep review={review} choices={choices} onChoose={choose} /> : null}
        {step === 4 && review !== null ? <ConfirmStep review={review} choices={choices} /> : null}
      </div>

      <Footer
        step={step}
        firstStep={firstStep}
        hold={hold}
        saving={saving}
        canSaveLater={canSaveLater}
        saveError={saveError}
        onReread={isStale(saveError) ? reread : null}
        onBack={() => {
          setSaveError("");
          setStep((step - 1) as CityStep);
        }}
        onContinue={() => setStep((step + 1) as CityStep)}
        onSaveLater={() => void submit(false)}
        onConfirm={() => void submit(true)}
      />
    </Shell>
  );
}

function Shell({ onClose, step, heading, children }: { onClose: () => void; step: CityStep; heading: RefObject<HTMLHeadingElement>; children: ReactNode }) {
  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} aria-hidden="true" />
      <div
        className="drawer wide qr-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="city-po-title"
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            onClose();
          }
        }}
      >
        <div className="drawer-head qr-drawer-head">
          <div className="qr-drawer-title">
            <h2 id="city-po-title" ref={heading} tabIndex={-1}>
              Read the City's PO
            </h2>
            <button type="button" className="btn drawer-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
          <Steps step={step} />
        </div>
        {children}
      </div>
    </>
  );
}

function Steps({ step }: { step: CityStep }) {
  return (
    <ol className="qr-steps" aria-label="Steps">
      {CITY_STEPS.map((item) => {
        const state = item.step < step ? "done" : item.step === step ? "current" : "todo";
        return (
          <li key={item.step} className={`qr-step ${state}`} aria-current={state === "current" ? "step" : undefined}>
            <span className="qr-step-dot" aria-hidden="true">
              {state === "done" ? "✓" : item.step}
            </span>
            <span className="qr-step-label">{item.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

function ReadError({ error, retry }: { error: string; retry: (() => void) | null }) {
  if (error === "") {
    return null;
  }
  return (
    <p className="notice bad" role="alert">
      {error}
      {retry !== null ? (
        <>
          {" "}
          <button type="button" className="go-link" onClick={retry}>
            Read it again
          </button>
        </>
      ) : null}
    </p>
  );
}

function DropStep({ onFile, reading, fileName }: { onFile: (file: File) => void; reading: boolean; fileName: string | null }) {
  return (
    <>
      <DropZone onFile={onFile} busy={reading} fileName={fileName} prompt="Drop the City's PO here" label="Choose the City's PO PDF" />
      <p className="cm-doc-note">A scanned PO cannot be read. Capture one of those by hand instead.</p>
    </>
  );
}
